import { feathers, Application } from '@feathersjs/client'
import socketio from '@feathersjs/socketio-client'
import io from 'socket.io-client'
import { useChatStore } from "../../state/chat-store"
import { useUserStore } from '@/state/user-store'
import * as env from '@/env'
import { ChannelImpl } from '../../../../shared-src/channel'
import { MessageService } from '../../../../shared-src/message-service'
import { AccountService } from '../../../../shared-src/account-service'
import { fetchChannels } from '@/state/chat-actions'

let feathersClient: Application | undefined

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		//	when vite HMR reloads this module, a new feathersClient is created, 
		//	but the underlying socket is still around. 
		//	as event listeners are attached to the service objects, 
		// 	and those are tied to the socket, not to the feathersClient instance,
		//	without the cleanup this would result in double-handling events.
		resetFeathersClient()
	})

	import.meta.hot.accept((/*newModule*/) => {
		//	nothing to do here, but we still need an accept callback, 
		//	otherwise the dispose callback wouldn't be called either.
	})
}

export function getFeathersClient(): Application {
	if (!feathersClient)
		feathersClient = createFeathersClient()
	return feathersClient
}

export function getMessageService(): MessageService {
	return getFeathersClient().service('messages') as unknown as MessageService
}

export function getAccountService(): AccountService {
	return getFeathersClient().service('account') as unknown as AccountService
}

export function resetFeathersClient(): void {
	if (feathersClient) {
		const socket = (feathersClient as any).io
		if (socket) {
			socket.close()
		}
		feathersClient = undefined
	}
}

function createFeathersClient(): Application {
	const socket = io(
		env.socketUrl,
		{
			withCredentials: true,
			transports: ['websocket']
		})

	feathersClient = feathers()
	const socketConnection = socketio(socket)
	feathersClient.configure(socketConnection)

	// unlike other services, we need to explicitly call .use() for `messages` and `account`,
	// because they have custom methods 
	// (messages => loadPage, account => createCheckoutSession, createCustomerPortalSession, deleteAccount)
	
	feathersClient.use(
		'messages',
		socketConnection.service('messages'),
		{ methods: ['create', 'loadPage', 'patch'] }
	)

	feathersClient.use(
		'account', 
		socketConnection.service('account'), 
		{ methods: ['createCheckoutSession', 'createCustomerPortalSession', 'deleteAccount'] }
	)

	const { addMessage, updateMessage, addChannel, removeChannel, updateChannel } = useChatStore.getState()

	feathersClient.service('messages').on('created', (message) => {
		addMessage(message)
	})

	feathersClient.service('messages').on('patched', (message) => {
		updateMessage(message)
	})

	feathersClient.service('channels').on('created', (channelData) => {
		const userId = useUserStore.getState().user?.userId
		const channel = new ChannelImpl(channelData, userId)
		addChannel(channel)
	})

	feathersClient.service('channels').on('patched', async (channel) => {
		try {
			if (!feathersClient)
				return
			const userId = useUserStore.getState().user?.userId
			const updatedChannelData = await feathersClient.service('channels').get(channel.id)
			const updatedChannel = new ChannelImpl(updatedChannelData, userId)
			updateChannel(updatedChannel)
		} catch (error) {
			console.error('Failed to refresh channel details after patch:', error)
		}
	})

	feathersClient.service('channels').on('removed', (channel) => {
		removeChannel(channel.id)
	})

	feathersClient.service('users').on('patched', async (patchedUser) => {
		if (patchedUser.subscriptionChanged) {
			const userId = useUserStore.getState().user?.userId
			if (userId && userId === patchedUser.userId) {
				try {
					await fetchChannels({ userId })
				} catch (error) {
					console.error('Failed to refresh channels after subscription change:', error)
				}
			}
		}
	})

	return feathersClient
}

// TODO move this to... somewhere else
export async function leaveGroup(channelId: string): Promise<void> {
	const client = getFeathersClient()
	const { removeChannel } = useChatStore.getState()

	try {
		await client.service('channels').patch(channelId, { action: 'leave' })
		removeChannel(channelId)
	} catch (error) {
		console.error('Failed to leave group:', error)
		throw error
	}
}
